(() => {
  'use strict';

  if (window.BureauAccount) return;
  const roots = [...document.querySelectorAll('[data-bo-account], [data-bo-mission]')];
  if (!roots.length) return;
  const origins = new Set([
    'https://api.bureauobscura.com',
    'https://bureau-obscura-staging.bureauobscura.workers.dev',
  ]);
  const configuredOrigin = document.body.getAttribute('data-bo-api-origin');
  const apiOrigin = origins.has(configuredOrigin)
    ? configuredOrigin : 'https://api.bureauobscura.com';
  let state = { status: 'loading', name: '', csrf: '', approved: false, frameKey: '' };
  let generation = 0;
  let requestController = null;
  let loggingOut = false;
  let profileController = null;
  let profileGeneration = 0;
  let savingName = false;
  let profileMessage = '';

  function cancelProfile() {
    ++profileGeneration;
    profileController?.abort();
    profileController = null;
    savingName = false;
    profileMessage = '';
  }

  function render(preserveFrame = false) {
    document.documentElement.classList.toggle('bo-mission-active',
      state.approved && roots.some((root) => root.hasAttribute('data-bo-mission')));
    for (const root of roots) {
      root.dataset.boSession = state.status;
      for (const node of root.querySelectorAll('[data-bo-session-view]')) {
        const view = node.getAttribute('data-bo-session-view');
        node.hidden = view === 'authenticated'
          ? state.status !== 'authenticated'
          : view === 'approved' ? !state.approved
            : view === 'unavailable'
              ? state.status !== 'authenticated' || state.approved
              : view !== state.status;
      }
      for (const node of root.querySelectorAll('[data-bo-agent-name]')) {
        node.textContent = state.name.replace(/^(?:agent[\s:]+)+/i, '') || state.name;
      }
      for (const button of root.querySelectorAll('[data-bo-sign-out]')) {
        button.disabled = loggingOut || !state.csrf;
      }
      for (const input of root.querySelectorAll('[data-bo-name-input]')) {
        if (!savingName) input.value = state.name;
        input.disabled = savingName || loggingOut || !state.csrf;
      }
      for (const button of root.querySelectorAll('[data-bo-save-name]')) {
        button.disabled = savingName || loggingOut || !state.csrf;
      }
      for (const node of root.querySelectorAll('[data-bo-profile-status]')) {
        node.textContent = profileMessage ? node.getAttribute(`data-bo-copy-${profileMessage}`) : '';
        node.setAttribute('role', ['invalid', 'failed'].includes(profileMessage) ? 'alert' : 'status');
      }
      const slot = root.querySelector('[data-bo-mission-frame]');
      if (!slot) continue;
      if (!state.approved) {
        if (!preserveFrame) slot.replaceChildren();
      } else {
        if (slot.firstElementChild?.dataset.boAccountKey !== state.frameKey) slot.replaceChildren();
        if (slot.firstElementChild) continue;
        const frame = document.createElement('iframe');
        frame.title = root.getAttribute('data-bo-frame-title');
        frame.src = `${apiOrigin}/missions/the-ninth-pane/`;
        frame.dataset.boAccountKey = state.frameKey;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        frame.allow = 'fullscreen';
        slot.appendChild(frame);
      }
    }
  }

  function clear(status = 'loading', preserveFrame = false) {
    state = { status, name: '', csrf: '', approved: false, frameKey: '' };
    render(preserveFrame);
  }

  async function refresh() {
    if (loggingOut || document.visibilityState === 'hidden') return;
    cancelProfile();
    const current = ++generation;
    requestController?.abort();
    requestController = new AbortController();
    const controller = requestController;
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    clear('loading', true);
    try {
      const response = await fetch(`${apiOrigin}/api/customer/session`, {
        credentials: 'include', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) throw new Error('session');
      const packet = await response.json();
      if (current !== generation) return;
      if (packet.authenticated === false) {
        clear('guest');
        return;
      }
      const name = packet.account?.agent_designator;
      if (packet.authenticated !== true || typeof name !== 'string' || !name.trim()) {
        throw new Error('session');
      }
      state = {
        status: 'authenticated', name: name.trim(),
        csrf: typeof packet.csrf_token === 'string' ? packet.csrf_token : '',
        approved: packet.missions?.ninth_pane === true,
        frameKey: typeof packet.missions?.storage_key === 'string' && packet.missions.storage_key
          ? packet.missions.storage_key : String(current),
      };
      render();
    } catch {
      if (current === generation) clear('error');
    } finally {
      window.clearTimeout(timeout);
      if (current === generation) requestController = null;
    }
  }

  function logoutDestination(value) {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value);
      const host = url.hostname;
      const trusted = url.origin === apiOrigin || host === 'bureauobscura.com'
        || host.endsWith('.bureauobscura.com') || host === 'shopify.com'
        || host.endsWith('.shopify.com') || host.endsWith('.myshopify.com');
      return url.protocol === 'https:' && !url.username && !url.password && trusted
        ? url.href : null;
    } catch { return null; }
  }

  async function signOut() {
    if (loggingOut || !state.csrf) return;
    window.dispatchEvent(new Event('bureau:account-changing'));
    const csrf = state.csrf;
    cancelProfile();
    loggingOut = true;
    ++generation;
    requestController?.abort();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    clear();
    try {
      const response = await fetch(`${apiOrigin}/api/customer/logout`, {
        method: 'POST', credentials: 'include', cache: 'no-store',
        headers: { Accept: 'application/json', 'X-Bureau-CSRF': csrf },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('logout');
      const packet = await response.json();
      if (packet.authenticated !== false) throw new Error('logout');
      clear('signed-out');
      const destination = logoutDestination(packet.logout_url);
      if (destination) window.location.assign(destination);
    } catch {
      clear('error');
    } finally {
      loggingOut = false;
      window.clearTimeout(timeout);
    }
  }

  async function saveName(form) {
    if (savingName || loggingOut || state.status !== 'authenticated' || !state.csrf) return;
    const input = form.querySelector('[data-bo-name-input]');
    const name = String(input?.value || '').trim().replace(/\s+/g, ' ');
    if (!name || name.length > 100) {
      profileMessage = 'invalid';
      render();
      input?.focus();
      return;
    }
    const current = generation;
    const profileRequest = ++profileGeneration;
    const csrf = state.csrf;
    profileController = new AbortController();
    const controller = profileController;
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const sameAccount = () => profileRequest === profileGeneration && current === generation
      && state.status === 'authenticated' && state.csrf === csrf;
    savingName = true;
    profileMessage = 'saving';
    render();
    try {
      const response = await fetch(`${apiOrigin}/api/customer/profile`, {
        method: 'POST', credentials: 'include', cache: 'no-store',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Bureau-CSRF': csrf },
        body: JSON.stringify({ agent_designator: name }), signal: controller.signal,
      });
      const packet = await response.json();
      if (!sameAccount()) return;
      if (response.status === 401) {
        cancelProfile();
        clear('guest');
        return;
      }
      const canonicalName = packet.account?.agent_designator;
      if (!response.ok || packet.authenticated !== true
        || typeof canonicalName !== 'string' || !canonicalName.trim()) throw new Error('profile');
      state = { ...state, name: canonicalName.trim() };
      profileMessage = 'saved';
    } catch {
      if (sameAccount()) profileMessage = 'failed';
    } finally {
      window.clearTimeout(timeout);
      if (profileRequest === profileGeneration) {
        savingName = false;
        profileController = null;
        render();
      }
    }
  }

  for (const root of roots) {
    for (const button of root.querySelectorAll('[data-bo-sign-out]')) {
      button.addEventListener('click', signOut);
    }
    for (const button of root.querySelectorAll('[data-bo-refresh-account]')) {
      button.addEventListener('click', refresh);
    }
    for (const form of root.querySelectorAll('[data-bo-name-form]')) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        void saveName(form);
      });
    }
  }
  window.addEventListener('pageshow', refresh);
  window.addEventListener('focus', refresh);
  window.addEventListener('pagehide', () => {
    ++generation;
    cancelProfile();
    requestController?.abort();
    clear();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh();
  });
  window.BureauAccount = Object.freeze({ refresh, signOut });
  void refresh();
})();
